<!-- MemberAccountCardModal.vue -->
<script setup>
import { computed, ref, watch } from "vue";
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import UserCardModal from "@/components/User/UserCardModal.vue";

// Instância da API para montar URL absoluta da foto
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";
import EditMemberAccountForm from "./EditMemberAccountForm.vue";
import DeleteMemberAccountForm from "./DeleteMemberAccountForm.vue";

const props = defineProps({
  show: { type: Boolean, required: true },
  // ID do relacionamento (managedAccountRelationshipId)
  relationshipId: { type: Number, required: true },
});

const emit = defineEmits(["close", "relationship-updated", "notify"]);

const managedAccountStore = useManagedAccountStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

// ── Estados para controle do EditMemberAccountForm / DeleteMemberAccountForm ─
const showEditModal = ref(false);
const showDeleteModal = ref(false);

function openEditModal() {
  if (relationship.value) {
    managedAccountStore.setSelectedRelationship(relationship.value);
  }
  showEditModal.value = true;
}

function handleEditSaved() {
  showEditModal.value = false;
  emit("relationship-updated");
}

function openDeleteModal() {
  if (relationship.value) {
    managedAccountStore.setSelectedRelationship(relationship.value);
  }
  showDeleteModal.value = true;
}

function handleDeleteRefresh() {
  showDeleteModal.value = false;
  emit("relationship-updated");
  emit("close");
}

function handleDeleteNotify(payload) {
  emit("notify", payload);
}

// ── Estados para controle do Modal de Usuário ────────────────────────────────
const showUserModal = ref(false);
const selectedUserId = ref(null);

function openUserModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserModal.value = true;
}

function closeUserModal() {
  showUserModal.value = false;
}

// ── Objeto do vínculo: prioriza selectedRelationship da store, ────────────────
// com fallback para a lista paginada (espelho do UserGroupCardModal)
const relationship = computed(() => {
  if (
    managedAccountStore.selectedRelationship &&
    managedAccountStore.selectedRelationship.managedAccountRelationshipId ===
      props.relationshipId
  ) {
    return managedAccountStore.selectedRelationship;
  }
  return (
    managedAccountStore.pagedRelationships.find(
      (r) => r.managedAccountRelationshipId === props.relationshipId,
    ) || null
  );
});

watch(
  () => props.relationshipId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    try {
      // Quando existir endpoint GET /managed-accounts/{id}, usar aqui:
      // await managedAccountStore.getRelationshipById(newId);
      isLoadingData.value = false;
    } catch (e) {
      console.error("Erro ao carregar vínculo no CardModal:", e);
      isLoadingData.value = false;
    }
  },
  { immediate: true },
);

// ── Helpers de avatar (mesmo padrão do UserGroupCardModal) ───────────────────
function normalizeAvatarPath(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

function getCreatorAvatarUrl(data) {
  if (!data) return "";
  const path =
    data.createdByUserPhotoPath || data.createdByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}

function getUpdaterAvatarUrl(data) {
  if (!data) return "";
  const path =
    data.updatedByUserPhotoPath || data.updatedByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}

// ── Helpers de exibição ──────────────────────────────────────────────────────

/** Badge de cor para relationship_type */
function relationshipTypeLabel(type) {
  if (!type) return "—";
  const map = {
    FamilyGuardian: "Responsável/Família",
    TeacherStudent: "Professor/Aluno",
  };
  return map[type] ?? type;
}

/** Badge de cor para status */
function statusLabel(status) {
  if (!status) return "—";
  const map = {
    Active: "Ativo",
    Inactive: "Inativo",
    Revoked: "Revogado",
  };
  return map[status] ?? status;
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="member-card-modal-content" :style="panelStyle">
      <!-- ── Cabeçalho ───────────────────────────────────────────────────── -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes do Vínculo de Conta Membro</h3>

        <div class="modal-header-actions">
          <button
            @click="emit('close')"
            type="button"
            class="modal-close-btn"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- ── Conteúdo ────────────────────────────────────────────────────── -->
      <div v-if="relationship && !isLoadingData" class="card-content-grid">
        <div class="details-col">

          <!-- Título: nome do membro + ID do vínculo -->
          <div class="title-row">
            <h3 class="text-title-bold">
              {{ toTitleCase(relationship.fullName || relationship.userName) }}
              <span class="title-id">
                (ID Vínculo: {{ relationship.managedAccountRelationshipId }})
              </span>
            </h3>

            <button
              type="button"
              class="btn-edit-member tooltip-bottom"
              data-tooltip="Editar vínculo"
              @click.stop="openEditModal"
            >
              <i class="fas fa-pencil-alt"></i>
            </button>

            <button
              type="button"
              class="btn-delete-member tooltip-bottom"
              data-tooltip="Apagar/revogar vínculo"
              @click.stop="openDeleteModal"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>

          <!-- ── Seção 1: Identificação do Membro ──────────────────────── -->
          <p class="section-label">Conta Membro</p>
          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">ID do Membro</span>
              <span class="meta-value user-id-value">
                #{{ relationship.managedUserId }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Username</span>
              <span class="meta-value">@{{ relationship.userName }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Nome Completo</span>
              <span class="meta-value">
                {{ toTitleCase(relationship.fullName) || "—" }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">E-mail</span>
              <span class="meta-value">{{ relationship.email }}</span>
            </div>
          </div>

          <hr class="content-separator" />

          <!-- ── Seção 2: Titular (Manager) ────────────────────────────── -->
          <p class="section-label">USERNAME DO TITULAR</p>
          <div class="meta-grid">
            <div class="meta-item meta-item-full">
              <!-- <span class="meta-label">Username do Titular</span> -->
              <span
                class="meta-value user-id-clickable"
                @click="openUserModal(relationship.managerUserId)"
                title="Ver perfil do titular"
              >
                @{{ relationship.managerUserName || "—" }}
                <span class="meta-id-inline">(ID: {{ relationship.managerUserId }})</span>
              </span>
            </div>
          </div>

          <hr class="content-separator" />

          <!-- ── Seção 3: Vínculo ───────────────────────────────────────── -->
          <p class="section-label">Dados do Vínculo</p>
          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">Perfil</span>
              <span
                class="meta-value"
                :class="[
 'badge',
                  relationship.relationshipType === 'FamilyGuardian'
                    ? 'badge--family'
                    : 'badge--teacher',
                ]"
              >
                {{ relationshipTypeLabel(relationship.relationshipType) }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Status</span>
              <span
                class="meta-value"
                :class="[
 'badge',
                  relationship.status === 'Active'
                    ? 'badge--active'
                    : relationship.status === 'Inactive'
                      ? 'badge--inactive'
                      : 'badge--revoked',
                ]"
              >
                {{ statusLabel(relationship.status) }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Criado em</span>
              <span class="meta-value">
                {{ formatDateTimeForDisplay(relationship.createdAt) }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Atualizado em</span>
              <span class="meta-value">
                {{ formatDateTimeForDisplay(relationship.updatedAt) }}
              </span>
            </div>
          </div>

          <hr class="content-separator" />

          <!-- ── Seção 4: Consentimento ─────────────────────────────────── -->
          <p class="section-label">Consentimento</p>
          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">Data do Consentimento</span>
              <span class="meta-value">
                {{ formatDateTimeForDisplay(relationship.consentGivenAt) }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">IP do Consentimento</span>
              <span class="meta-value mono-value">
                {{ relationship.consentIpAddress || "—" }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Versão dos Termos</span>
              <span class="meta-value">
                {{ relationship.termsVersion || "—" }}
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Versão da Privacidade</span>
              <span class="meta-value">
                {{ relationship.privacyVersion || "—" }}
              </span>
            </div>
            <div v-if="relationship.consentUserAgent" class="meta-item meta-item-full">
              <span class="meta-label">User-Agent</span>
              <span class="meta-value meta-value-small mono-value">
                {{ relationship.consentUserAgent }}
              </span>
            </div>
            <div
              v-if="relationship.consentTextSnapshot"
              class="meta-item meta-item-full"
            >
              <span class="meta-label">Texto de Consentimento</span>
              <div class="consent-snapshot">
                {{ relationship.consentTextSnapshot }}
              </div>
            </div>
          </div>

          <hr class="content-separator" />

          <!-- ── Seção 5: Auditoria (Criador / Atualizador) ─────────────── -->
          <div class="footer-info">
            <div class="users-row">
              <!-- Criador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(relationship.createdByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getCreatorAvatarUrl(relationship)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getCreatorAvatarUrl(relationship)}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Criador</span>
                  <span class="chip-name">
                    {{ relationship.createdByUserName || "Não informado" }}
                  </span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(relationship.createdAt) }}
                  </span>
                </div>
              </div>

              <!-- Atualizador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(relationship.updatedByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getUpdaterAvatarUrl(relationship)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getUpdaterAvatarUrl(relationship)}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Atualizador</span>
                  <span class="chip-name">
                    {{ relationship.updatedByUserName || "Não informado" }}
                  </span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(relationship.updatedAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- ── Loading ────────────────────────────────────────────────────── -->
      <div v-else class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes do vínculo...
      </div>

    </div>
  </div>

  <!-- Sub-modais via Teleport.
       Todos são position: fixed e o painel deste card ganha transform ao ser
       arrastado — um ancestral com transform viraria o containing block deles,
       fazendo o overlay cobrir só o painel e acompanhar o arrasto. Fora do
       painel no DOM, seguem ancorados na viewport. -->
  <Teleport to="body">
    <UserCardModal
      v-if="showUserModal"
      :show="showUserModal"
      :userId="selectedUserId"
      @close="closeUserModal"
    />

    <EditMemberAccountForm
      v-if="showEditModal"
      :relationship-id="props.relationshipId"
      @close="showEditModal = false"
      @saved="handleEditSaved"
    />

    <DeleteMemberAccountForm
      v-if="showDeleteModal && relationship"
      :relationship="relationship"
      @close="showDeleteModal = false"
      @refresh="handleDeleteRefresh"
      @notify="handleDeleteNotify"
    />
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
  align-items: center;
}

.member-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  position: relative;
  box-shadow: var(--shadow-modal);
  /* altura fixa: viewport menos 20% de margem (10% top + 10% bottom) */
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Os botões do cabeçalho continuam sendo clique, não alça */
.modal-header--draggable .modal-close-btn,
.modal-header--draggable .modal-header-actions button {
  cursor: pointer;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.card-content-grid {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.details-col {
  width: 100%;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 10px;
}

.btn-edit-member,
.btn-delete-member {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  position: relative;
}

.btn-edit-member {
  border: 1px solid var(--info-border);
  background-color: var(--info-bg);
  color: var(--info-text);
}

.btn-edit-member:hover {
  background-color: var(--info-border);
}

.btn-delete-member {
  border: 1px solid var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-text);
}

.btn-delete-member:hover {
  background-color: var(--danger-bg);
}

.btn-edit-member i,
.btn-delete-member i {
  font-size: 0.6rem;
}

.tooltip-bottom {
  position: relative;
}

.tooltip-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(-2px);
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 80;
}

.tooltip-bottom:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 0.8s;
}

.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 10px 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

/* Rótulos de seção */
.section-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 8px 0;
}

.meta-grid {
  grid-template-columns: 1fr 1fr;
}

.meta-label {
  margin-bottom: 2px;
}

.meta-value-small {
  font-size: 0.78rem;
  word-break: break-all;
}

.mono-value {
  font-family: ui-monospace, "Cascadia Code", "Source Code Pro", monospace;
  font-size: 0.82rem;
  color: var(--text-dark);
}

/* IDs clicáveis */
.user-id-value {
  font-weight: 600;
  color: #4f46e5;
}

.user-id-clickable {
  cursor: pointer;
  text-decoration: underline dotted;
  transition: color 0.15s;
}

.user-id-clickable:hover {
  color: #4338ca;
}

/* Badges de tipo e status */
.badge {
  display: inline-block;
  width: fit-content;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge--family {
  background: var(--brand-soft);
  color: var(--text-on-brand-soft);
}

.badge--teacher {
  background: var(--info-border);
  color: var(--link-hover);
}

.badge--active {
  background: var(--success-bg);
  color: var(--success-text);
}

.badge--inactive {
  background: var(--warning-bg);
  color: #854d0e;
}

.badge--revoked {
  background: var(--danger-bg);
  color: var(--danger-text);
}

/* Snapshot do texto de consentimento */
.consent-snapshot {
  font-size: 0.8rem;
  color: var(--warning-text);
  background: #fff9f0;
  border: 1px solid #f3e2cf;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  line-height: 1.5;
  margin-top: 4px;
}

.footer-info {
  padding-top: 6px;
  width: 100%;
}

.users-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
}

/* ID inline ao lado do username do titular */
.meta-id-inline {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-muted);
  margin-left: 4px;
}

</style>